import React, {useContext, useEffect, useState, useRef, useCallback} from 'react';
import {Context} from "../../index";
import {fetchUserLogs, markLogsAsRead} from "../../http/userLogAPI";
import {Badge, Button, Card, Pagination, Table} from "react-bootstrap";
import Loader from "../Loader";
import {check} from "../../http/userAPI";

const VirtUserLogsInDashboard = ({ refreshKey = 0 }) => {
    const { user } = useContext(Context);
    const [logs, setLogs] = useState([]);
    const [logsCount, setLogsCount] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [markingRead, setMarkingRead] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const totalPages = Math.max(1, Math.ceil(logsCount / limit));

    // раскрытые строки
    const [openRows, setOpenRows] = useState(new Set());
    const toggleRow = (id) =>
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                clearRowTimer(id); // сворачиваем — чистим таймер
            } else {
                next.add(id);
                const log = logs.find(l => l.id === id);
                if (log) scheduleMarkRowRead(log);  // раскрыли — ставим таймер 5с
            }
            return next;
        });

    const isOpen = (id) => openRows.has(id);

    const loadLogs = useCallback(async ({ showMainLoader = false } = {}) => {
        if (!user?.user?.id) return;
        if (showMainLoader) setLoading(true);

        const data = await fetchUserLogs(user.user.id, { page, limit });
        const items = Array.isArray(data) ? data : (data?.items || []);

        setLogs(items);
        setLogsCount(Number(Array.isArray(data) ? data.length : data?.count || 0));
        setOpenRows(new Set());
        if (showMainLoader) setLoading(false);
    }, [user?.user?.id, page, limit]);

    useEffect(() => {
        if (user.activeComponent === "VirtBalanceLogs") {
            loadLogs({ showMainLoader: true }).catch((error) => {
                console.error("Error fetching user logs:", error);
                setLoading(false);
            });
        }
    }, [user.user.id, user.activeComponent, refreshKey, page, loadLogs]);

    useEffect(() => {
        const unread = logs.reduce((acc, l) => acc + (l?.is_read ? 0 : 1), 0);
        // Если в сторе есть текущее значение — сверим, чтобы лишний раз не сетать
        if (typeof user.readCountVirt === 'number') {
            if (user.readCountVirt !== unread) user.setReadCountVirt(unread);
        } else {
            user.setReadCountVirt(unread);
        }
    }, [logs]);

    // авто-пометка прочитанных
    const rowTimersRef = useRef(new Map());

    const scheduleMarkRowRead = useCallback((log) => {
        if (log.is_read) return;
        if (rowTimersRef.current.has(log.id)) return;

        const tid = setTimeout(async () => {
            try {
                await markLogsAsRead(user.user.id, log.id);
                setLogs(prev => {
                    const next = prev.map(l => (l.id === log.id ? { ...l, is_read: true } : l));
                    const unread = next.reduce((acc, l) => acc + (l?.is_read ? 0 : 1), 0);
                    user.setReadCountVirt(unread); // <- сразу после пересчёта
                    return next;
                });
            } finally {
                rowTimersRef.current.delete(log.id);
            }
        }, 2000);

        rowTimersRef.current.set(log.id, tid);
    }, [user.user.id]);


    const clearRowTimer = useCallback((logId) => {
        const tid = rowTimersRef.current.get(logId);
        if (tid) {
            clearTimeout(tid);
            rowTimersRef.current.delete(logId);
        }
    }, []);



    const handleMarkAsRead = useCallback(async () => {
        if (markingRead) return;
        setMarkingRead(true);
        try {
            await markLogsAsRead(user.user.id);
            setLogs(prev => {
                const next = prev.map(log => ({ ...log, is_read: true }));
                user.setReadCountVirt(0);
                return next;
            });
        } finally {
            setMarkingRead(false);
        }
    }, [markingRead, user.user.id]);


    const updateUserData = async () => {
        try {
            setRefreshing(true);
            await loadLogs();
            const dataU = await check();
            user.setUser(dataU);
        } catch (e) {
            console.error("Error fetching user logs:", e);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        return () => {
            rowTimersRef.current.forEach(tid => clearTimeout(tid));
            rowTimersRef.current.clear();
        };
    }, []);

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">
                <h5 className="text-muted mb-2 mb-md-0 me-md-3 text-nowrap">
                    Balance Logs on Virtalibry
                </h5>

                <div className="d-flex w-100 justify-content-between gap-2 w-md-auto justify-content-md-end ms-md-auto">
                    <Button
                        variant="outline-secondary"
                        onClick={updateUserData}
                        className="btn-sm"
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        ) : null}
                        Refresh Data
                    </Button>

                    {logs.length > 0 && (
                        <Button
                            variant="outline-success"
                            onClick={handleMarkAsRead}
                            disabled={markingRead}
                            className="btn-sm"
                        >
                            {markingRead ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                            ) : null}
                            Mark All as Read
                        </Button>
                    )}
                </div>
            </div>

            <div className="text-muted small mt-2">
                <p className="mb-0">
                    This section displays your full history of Virt balance changes — including earnings from referrals (Virt model), promo code usage, bonuses, and deductions.
                </p>
                <p className="mb-3">
                    Each entry gives you a transparent view of what affected your balance, when it happened, and why. Whether you're gaining rewards from partner activity or spending Virt on assignments, this log helps you track your progress and maintain control of your finances. It’s part of our commitment to building a smarter, fairer author ecosystem.
                </p>
            </div>

            {loading ? (
                <div className="text-center">
                    <Loader animation="border" />
                </div>
            ) : logs.length > 0 ? (
                <>
                    {/* 📱 Mobile cards (xs–sm): label — value в одну строку, space-between */}
                    <div className="d-md-none">
                        {logs.map((log) => {
                            const change =
                                Number(log.amount) === 0
                                    ? "Unchanged"
                                    : `${log.amount > 0 ? "+" : ""}${Math.round(log.amount)} Virt`;
                            const balance = `${Math.round(log.balance_state)} Virt`;
                            const dateStr = new Date(log.user_log_date * 1000).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                            });

                            return (
                                <div
                                    key={log.id}
                                    className={`card shadow-sm mb-2 ${isOpen(log.id) ? 'border-success' : ''}`}
                                    role="button"
                                    aria-expanded={isOpen(log.id)}
                                    onClick={() => toggleRow(log.id)}
                                >
                                    <div className="card-body py-2">

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">
                                                {isOpen(log.id) ? '▾ ' : '▸ '}Change
                                            </small>
                                            <span className="small text-end text-truncate" style={{maxWidth:'65%'}}>{change}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Balance</small>
                                            <span className="small text-end text-truncate" style={{maxWidth:'65%'}}>{balance}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Description</small>
                                            <span className="text-end small text-truncate" style={{maxWidth:'65%'}}>{log.description || "—"}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Date</small>
                                            <span className="text-end small text-truncate" style={{maxWidth:'65%'}}>{dateStr}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Status</small>
                                            <span className="text-end small">
                        {!log.is_read ? <Badge bg="success">New</Badge> : <span className="text-muted">Read</span>}
                      </span>
                                        </div>

                                        {isOpen(log.id) && (
                                            <div className="mt-2 p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                <div className="text-muted small mb-1">Full text of this log:</div>
                                                <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
                                                    {log.description || "—"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* 💻 Desktop table (md+): кликабельные строки, как было */}
                    <div className="d-none d-md-block" style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                        <Table bordered hover size="sm" striped className="mt-1" style={{ minWidth: '600px', lineHeight: '1.3' }}>
                            <thead className="table-light text-muted small">
                            <tr className="bg-secondary text-center bg-opacity-50 text-sm">
                                <th className="small text-muted">Change:</th>
                                <th className="small text-muted">Balance:</th>
                                <th className="small text-muted">Description:</th>
                                <th className="small text-muted">Date:</th>
                                <th className="small text-muted">Status:</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logs.map((log) => (
                                <React.Fragment key={log.id}>
                                    <tr
                                        onClick={() => toggleRow(log.id)}
                                        style={{ cursor: "pointer" }}
                                        className={isOpen(log.id) ? "table-active small fst-italic" : "small fst-italic"}
                                        role="button"
                                        aria-expanded={isOpen(log.id)}
                                    >
                                        <td className="text-center small text-success fw-bold align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {isOpen(log.id) ? "▾ " : "▸ "}
                                            {Number(log.amount) === 0 ? "Unchanged" : `${log.amount > 0 ? "+" : ""}${Math.round(log.amount)} Virt`}
                                        </td>
                                        <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {Math.round(log.balance_state)} Virt
                                        </td>
                                        <td className="small align-middle text-muted" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "600px", width: "600px" }}>
                                            {log.description || "—"}
                                        </td>
                                        <td className="text-center small text-muted align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {new Date(log.user_log_date * 1000).toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: false,
                                            })}
                                        </td>
                                        <td className="text-center align-items-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {!log.is_read ? <span className="badge bg-success">New</span> : <span className="text-muted">Read</span>}
                                        </td>
                                    </tr>

                                    {isOpen(log.id) && (
                                        <tr className="small">
                                            <td colSpan={5} className="text-start py-2">
                                                <div className="p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                    <div className="text-muted fw-semibold mb-1">Full text of this log:</div>
                                                    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                                        {log.description || "—"}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </Table>
                    </div>

                    {logsCount > limit && (
                        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 mt-2">
                            <div className="small text-muted">
                                Showing {(page - 1) * limit + 1}-{Math.min(page * limit, logsCount)} of {logsCount} logs
                            </div>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.First
                                    disabled={page <= 1 || loading || refreshing}
                                    onClick={() => setPage(1)}
                                />
                                <Pagination.Prev
                                    disabled={page <= 1 || loading || refreshing}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                />
                                <Pagination.Item
                                    active
                                    linkStyle={{
                                        backgroundColor: "#6c757d",
                                        borderColor: "#6c757d",
                                        color: "#fff",
                                    }}
                                >
                                    {page} / {totalPages}
                                </Pagination.Item>
                                <Pagination.Next
                                    disabled={page >= totalPages || loading || refreshing}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                />
                                <Pagination.Last
                                    disabled={page >= totalPages || loading || refreshing}
                                    onClick={() => setPage(totalPages)}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-center text-muted">No Logs</p>
            )}
        </Card>
    );
};

export default VirtUserLogsInDashboard;
